import { useTranslation } from 'react-i18next'
import { Label } from '@/shared/components/ui/label'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/shared/components/ui/select'
import { Switch } from '@/shared/components/ui/switch'
import { usePaymentRules } from '../lib/use-payment-rules'

/**
 * Radix refuses an empty string as an item value, so the "no integration" row needs a word of its own.
 * It never reaches the server: the caller turns it back into null on the way out.
 */
const NO_RULE = '__none__'

export interface WizardPaymentsSectionProps {
  paymentsEnabled: boolean
  onPaymentsEnabledChange: (enabled: boolean) => void
  /** Null is the normal value: most accounts disperse nothing at all. */
  paymentRuleId: number | null
  onPaymentRuleChange: (paymentRuleId: number | null) => void
}

/**
 * Where an account is told whether it pays, and by which channel.
 *
 * The order is the order the decision is made in: the switch first, and the channel only once payments
 * are on. With it off there is nothing to configure and nothing is shown — which is the state of 52 of
 * the 54 active accounts in QA, so it is the common case and not the edge one.
 *
 * Hiding the channel like that is only honest because the switch now MEANS something: with it off the
 * core will not let a payment be created, saved or dispatched from this account. While it merely
 * disabled the create form, a hidden channel would have been a screen saying «no payments here» over an
 * account that paid perfectly well.
 */
export function WizardPaymentsSection({
  paymentsEnabled, onPaymentsEnabledChange, paymentRuleId, onPaymentRuleChange,
}: WizardPaymentsSectionProps) {
  const { t } = useTranslation()
  const { data: rules = [], isPending } = usePaymentRules()

  const selected = rules.find((rule) => rule.Id === paymentRuleId) ?? null

  /*
   * The account's own rule when the catalogue no longer offers it — the integration was disconnected,
   * or th_services could not be asked at all. It is kept in the list so the select shows what the
   * account actually has instead of silently reading as «no integration», which is a different account
   * from this one and would be saved as such by anyone who pressed save.
   */
  const orphaned = paymentRuleId !== null && selected === null

  return (
    <div className="flex flex-col gap-4">
      <div className="h-px bg-border" />

      <h3 className="text-[15px] font-semibold tracking-tight">
        {t('bankAccounts.payments.heading')}
      </h3>

      <div className="flex items-start gap-3">
        <Switch
          id="paymentsEnabled"
          className="mt-0.5"
          checked={paymentsEnabled}
          onCheckedChange={onPaymentsEnabledChange}
        />
        <div className="flex flex-col gap-1">
          <Label htmlFor="paymentsEnabled">{t('bankAccounts.payments.enabled')}</Label>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {t('bankAccounts.payments.enabledHint')}
          </p>
        </div>
      </div>

      {paymentsEnabled && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="dispersionChannel">{t('bankAccounts.payments.channel')}</Label>

          <Select
            value={paymentRuleId === null ? NO_RULE : String(paymentRuleId)}
            onValueChange={(value) => onPaymentRuleChange(value === NO_RULE ? null : Number(value))}
          >
            <SelectTrigger id="dispersionChannel">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* Always first and always present, because it is what most accounts legitimately are. */}
              <SelectItem value={NO_RULE}>{t('bankAccounts.payments.noIntegration')}</SelectItem>

              {orphaned && (
                <SelectItem value={String(paymentRuleId)}>
                  {t('bankAccounts.payments.currentUnavailable')}
                </SelectItem>
              )}

              {rules.map((rule) => (
                <SelectItem key={rule.Id} value={String(rule.Id)}>
                  {rule.PaymentType}
                  <span className="ml-2 text-xs text-muted-foreground">{rule.ChannelCode}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <p className="text-xs leading-relaxed text-muted-foreground">
            {t('bankAccounts.payments.channelHint')}
          </p>

          {/*
           * An empty catalogue and a catalogue that could not be read look the same on purpose: the core
           * answers 200 with nothing either way, so the screen never has to guess which it was. What it
           * can say truthfully is that there is nothing to choose, and where that gets fixed.
           */}
          {!isPending && rules.length === 0 && (
            <p className="text-xs leading-relaxed text-destructive">
              {t('bankAccounts.payments.noneAvailable')}
            </p>
          )}

          {selected !== null && !selected.Connected && (
            <p className="text-xs leading-relaxed text-muted-foreground">
              {t('bankAccounts.payments.notConnectedYet', { channel: selected.PaymentType })}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
